import { Grid, Stack, Typography } from "@mui/material";
import { IBroadbandAddon, ISubscribedAddon } from "./interface/IAddonDetails";
import { isActiveAddon, isOngoingAddon, isTerminatedAddon } from "./utils";
import { Trans, useTranslation } from "react-i18next";
import { AddAddon } from "./AddAddon";
import { ActiveAddonStatus } from "./ActiveAddonStatus";
import { CancelAddonStatus } from "./CancelAddonStatus";
import { useStyle } from "./style";
import { useDeviceType } from "@hook/useDeviceType";
import { OngoingAddonStatus } from "./OngoingAddonStatus";
import { OngoingDeliveryAddon } from "./OngoingDeliveryAddon";
import GOMO_WIFI_MESH_6 from "@static/img/addons/GOMO_WIFI_MESH_6.png";

interface IParams {
    broadbandAddon: IBroadbandAddon;
    subscribedAddon?: ISubscribedAddon;
    onOpenModal(): void;
}

export const Addon = (props: IParams) => {
    const {
        broadbandAddon: { code, description, price: broadbandAddonPrice },
        subscribedAddon,
        onOpenModal,
    } = props;
    const price = subscribedAddon?.price ?? broadbandAddonPrice;
    const { classes } = useStyle({});
    const { t } = useTranslation(["addons", "common", "glossary"]);
    const { isMobile } = useDeviceType();

    const renderAddonStatus = () => {
        if (isOngoingAddon(subscribedAddon)) {
            return <OngoingAddonStatus description={description} />;
        } else if (isActiveAddon(subscribedAddon)) {
            return <ActiveAddonStatus description={description} onOpenRemoveAddonModal={onOpenModal} />;
        } else if (isTerminatedAddon(subscribedAddon)) {
            return (
                <CancelAddonStatus
                    description={description}
                    shouldReturnEquipments={subscribedAddon.shouldReturnEquipments}
                />
            );
        }

        return <AddAddon description={description} onOpenModal={onOpenModal} />;
    };

    const renderAddon = () => (
        <Grid
            className={classes.addonContainer}
            columnSpacing={{ xs: 0, sm: 2 }}
            container
            rowSpacing={{ xs: 2, sm: 1 }}
        >
            <Grid item xs={8}>
                <Stack className={classes.addonDescriptionContainer}>
                    <Typography color="primary" variant="h3">
                        {description}
                    </Typography>
                </Stack>
            </Grid>
            <Grid item xs={4}>
                <Stack className={classes.addonPriceContainer} spacing={-0.4}>
                    <Typography color="primary" variant="h4">
                        {t("common:price", { price })}
                    </Typography>
                    <Typography color="secondary" variant="small">
                        {t("glossary:aMonth")}
                    </Typography>
                </Stack>
            </Grid>
            <Grid item sm={8} xs={12}>
                <Stack spacing={1}>
                    <Typography color="secondary" fontWeight={400} variant="normal">
                        <Trans i18nKey="addonsDescription.GOMO_WIFI_MESH_6" ns="addons" />
                    </Typography>
                    {isOngoingAddon(subscribedAddon) && (
                        <OngoingDeliveryAddon deliveryAddress={subscribedAddon.deliveryAddress} />
                    )}
                </Stack>
            </Grid>
            <Grid item sm={4} xs={12}>
                {renderAddonStatus()}
            </Grid>
        </Grid>
    );

    if (isMobile) {
        return renderAddon();
    }

    return (
        <Stack direction="row">
            <div className={classes.addonImageContainer}>
                <img alt={code} src={GOMO_WIFI_MESH_6} />
            </div>
            {renderAddon()}
        </Stack>
    );
};
